<script setup>
import { ref, computed } from 'vue'
import PhoneInput from '@/Components/Forms/PhoneInput.vue'
import { Head } from '@inertiajs/vue3'
import axios from 'axios'
import { Button } from '@/Components/ui/button'
import { Alert, AlertDescription } from '@/Components/ui/alert'
import { Home, Upload, X, CheckCircle, AlertCircle, Loader2 } from 'lucide-vue-next'

const form = ref({
  fullname: '',
  phone_1: '',
  phone_2: '',
  email: '',
  room: '',
  tenancy_start_date: '',
  emergency_contact: '',
})

const documents = ref({
  tenancy_agreement: [],
  identification: [],
  university_enrollment: [],
  payment_evidence: [],
})

const uploadingFiles = ref({})
const errors = ref({})
const isSubmitting = ref(false)
const isSubmitted = ref(false)
const generalError = ref('')

const hasErrors = computed(() => Object.keys(errors.value).length > 0 || generalError.value)

const documentTypes = [
  { key: 'tenancy_agreement', label: 'Signed Tenancy Agreement', required: true },
  { key: 'identification', label: 'Identification Document', required: true },
  { key: 'university_enrollment', label: 'Proof of University Enrollment (Receipt/Admission Offer)', required: true },
  { key: 'payment_evidence', label: 'Payment Evidence', required: true },
]

const rooms = Array.from({ length: 15 }, (_, i) => i + 1)

const validateFile = (file) => {
  const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png']
  const maxSize = 10 * 1024 * 1024 // 10MB

  if (!validTypes.includes(file.type)) {
    return 'Only PDF, JPG, and PNG files are allowed.'
  }
  if (file.size > maxSize) {
    return 'File size must be less than 10MB.'
  }
  return null
}

const handleFileSelect = async (type, event) => {
  const selectedFiles = Array.from(event.target.files)
  delete errors.value[type]

  for (const file of selectedFiles) {
    const validationError = validateFile(file)
    if (validationError) {
      errors.value[type] = validationError
      continue
    }

    // Generate a unique key for tracking upload progress
    const uploadKey = `${type}_${Date.now()}_${file.name}`
    uploadingFiles.value[uploadKey] = {
      name: file.name,
      size: file.size,
      progress: 0,
    }

    try {
      // Get presigned URL
      const presignedResponse = await axios.post('/tenant/presigned-upload-url', {
        filename: file.name,
        content_type: file.type,
        type: type,
      })

      const { upload_url, path, public_url } = presignedResponse.data.data

      // Upload directly to R2
      await axios.put(upload_url, file, {
        headers: {
          'Content-Type': file.type,
        },
        onUploadProgress: (progressEvent) => {
          if (uploadingFiles.value[uploadKey]) {
            uploadingFiles.value[uploadKey].progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            )
          }
        },
      })

      // Add to documents array
      documents.value[type].push({
        path: path,
        original_name: file.name,
        url: public_url,
        size: file.size,
      })

    } catch (error) {
      console.error('Upload error:', error)
      errors.value[type] = error.response?.data?.message || 'Failed to upload file. Please try again.'
    } finally {
      delete uploadingFiles.value[uploadKey]
    }
  }

  // Reset the input
  event.target.value = ''
}

const removeFile = (type, index) => {
  documents.value[type].splice(index, 1)
}

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

const isUploading = computed(() => Object.keys(uploadingFiles.value).length > 0)

const submit = async () => {
  errors.value = {}
  generalError.value = ''

  // Validate all document types have at least one file
  for (const docType of documentTypes) {
    if (documents.value[docType.key].length === 0) {
      errors.value[`documents.${docType.key}`] = `Please upload at least one ${docType.label.toLowerCase()}.`
    }
  }

  if (Object.keys(errors.value).length > 0) {
    return
  }

  isSubmitting.value = true

  try {
    await axios.post('/tenant/register', {
      fullname: form.value.fullname,
      phone_1: form.value.phone_1,
      phone_2: form.value.phone_2 || null,
      email: form.value.email || null,
      room: form.value.room,
      tenancy_start_date: form.value.tenancy_start_date,
      emergency_contact: form.value.emergency_contact,
      documents: documents.value,
    })

    isSubmitted.value = true
  } catch (error) {
    if (error.response?.status === 422) {
      errors.value = error.response.data.errors || {}
    } else {
      generalError.value = 'An error occurred. Please try again.'
    }
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <Head title="Tenant Registration - Blue House" />

  <div class="min-h-screen bg-gradient-to-br from-teal-50 to-teal-100 py-8 px-4">
    <div class="max-w-2xl mx-auto">
      <!-- Success Message -->
      <div v-if="isSubmitted" class="bg-white rounded-2xl shadow-xl p-8 text-center">
        <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle class="w-10 h-10 text-green-600" />
        </div>
        <h1 class="text-2xl font-bold text-gray-900 mb-2">Registration Submitted!</h1>
        <p class="text-gray-600 mb-6">
          Thank you for completing your tenant registration. We have received your information and documents.
        </p>
        <p class="text-sm text-gray-500">
          You will be contacted if any additional information is needed.
        </p>
      </div>

      <!-- Registration Form -->
      <div v-else class="bg-white rounded-2xl shadow-xl overflow-hidden">
        <!-- Header -->
        <div class="bg-gradient-to-r from-teal-500 to-teal-800 px-8 py-6">
          <div class="flex items-center space-x-3 mb-2">
            <div class="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Home class="w-6 h-6 text-white" />
            </div>
            <h1 class="text-2xl font-bold text-white">BLUE HOUSE</h1>
          </div>
          <p class="text-blue-100 text-sm">Tenant Registration</p>
          <p class="text-blue-200 text-xs mt-2">Please complete this form to the best of your ability</p>
        </div>

        <form @submit.prevent="submit" class="p-8 space-y-6">
          <!-- General Error -->
          <Alert v-if="hasErrors" variant="destructive">
            <AlertCircle class="h-4 w-4" />
            <AlertDescription>
              {{ generalError || 'Please correct the errors below.' }}
            </AlertDescription>
          </Alert>

          <!-- Fullname -->
          <div>
            <label for="fullname" class="block text-sm font-semibold text-gray-700 mb-2">
              Fullname <span class="text-red-500">*</span>
            </label>
            <input
              id="fullname"
              type="text"
              v-model="form.fullname"
              required
              :class="[
                'w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all',
                errors.fullname ? 'border-red-500' : 'border-gray-200'
              ]"
            />
            <p v-if="errors.fullname" class="text-sm text-red-500 mt-1">{{ Array.isArray(errors.fullname) ? errors.fullname[0] : errors.fullname }}</p>
          </div>

          <!-- Phone 1 -->
          <div>
            <label for="phone_1" class="block text-sm font-semibold text-gray-700 mb-2">
              Phone 1 <span class="text-red-500">*</span>
            </label>
            <PhoneInput v-model="form.phone_1" :invalid="Boolean(errors.phone_1)" />
            <p v-if="errors.phone_1" class="text-sm text-red-500 mt-1">{{ Array.isArray(errors.phone_1) ? errors.phone_1[0] : errors.phone_1 }}</p>
          </div>

          <!-- Phone 2 -->
          <div>
            <label for="phone_2" class="block text-sm font-semibold text-gray-700 mb-2">
              Phone 2
            </label>
            <PhoneInput v-model="form.phone_2" :invalid="Boolean(errors.phone_2)" />
            <p v-if="errors.phone_2" class="text-sm text-red-500 mt-1">{{ Array.isArray(errors.phone_2) ? errors.phone_2[0] : errors.phone_2 }}</p>
          </div>

          <!-- Email -->
          <div>
            <label for="email" class="block text-sm font-semibold text-gray-700 mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              v-model="form.email"
              :class="[
                'w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all',
                errors.email ? 'border-red-500' : 'border-gray-200'
              ]"
            />
            <p v-if="errors.email" class="text-sm text-red-500 mt-1">{{ Array.isArray(errors.email) ? errors.email[0] : errors.email }}</p>
          </div>

          <!-- Room -->
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-2">
              Room
            </label>
            <div class="grid grid-cols-5 gap-2">
              <button
                v-for="roomNum in rooms"
                :key="roomNum"
                type="button"
                @click="form.room = form.room === `Room ${roomNum}` ? '' : `Room ${roomNum}`"
                :class="[
                  'py-3 px-4 rounded-xl border-2 font-medium text-sm transition-all',
                  form.room === `Room ${roomNum}`
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'bg-white border-gray-200 text-gray-700 hover:border-blue-400 hover:bg-blue-50'
                ]"
              >
                {{ roomNum }}
              </button>
            </div>
            <p v-if="form.room" class="text-xs text-gray-500 mt-2">Selected: {{ form.room }} (click again to deselect)</p>
            <p v-if="errors.room" class="text-sm text-red-500 mt-1">{{ Array.isArray(errors.room) ? errors.room[0] : errors.room }}</p>
          </div>

          <!-- Tenancy Start Date -->
          <div>
            <label for="tenancy_start_date" class="block text-sm font-semibold text-gray-700 mb-2">
              Tenancy Start Date <span class="text-red-500">*</span>
            </label>
            <input
              id="tenancy_start_date"
              type="date"
              v-model="form.tenancy_start_date"
              required
              :class="[
                'w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all',
                errors.tenancy_start_date ? 'border-red-500' : 'border-gray-200'
              ]"
            />
            <p v-if="errors.tenancy_start_date" class="text-sm text-red-500 mt-1">{{ Array.isArray(errors.tenancy_start_date) ? errors.tenancy_start_date[0] : errors.tenancy_start_date }}</p>
          </div>

          <!-- Emergency Contact -->
          <div>
            <label for="emergency_contact" class="block text-sm font-semibold text-gray-700 mb-2">
              Emergency Contact (Name and Phone) <span class="text-red-500">*</span>
            </label>
            <input
              id="emergency_contact"
              type="text"
              v-model="form.emergency_contact"
              required
              placeholder="John Doe - +1234567890"
              :class="[
                'w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all',
                errors.emergency_contact ? 'border-red-500' : 'border-gray-200'
              ]"
            />
            <p v-if="errors.emergency_contact" class="text-sm text-red-500 mt-1">{{ Array.isArray(errors.emergency_contact) ? errors.emergency_contact[0] : errors.emergency_contact }}</p>
          </div>

          <!-- Document Uploads -->
          <div v-for="docType in documentTypes" :key="docType.key" class="space-y-2">
            <label class="block text-sm font-semibold text-gray-700">
              {{ docType.label }} <span v-if="docType.required" class="text-red-500">*</span>
            </label>
            <p class="text-xs text-gray-500 mb-2">Upload supported files (PDF, JPG, PNG). Max 10 MB each.</p>

            <!-- Uploaded Files List -->
            <div v-if="documents[docType.key].length > 0" class="space-y-2 mb-3">
              <div
                v-for="(doc, index) in documents[docType.key]"
                :key="index"
                class="flex items-center justify-between bg-green-50 rounded-lg px-4 py-2 border border-green-200"
              >
                <div class="flex items-center space-x-3 min-w-0">
                  <div class="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle class="w-4 h-4 text-green-600" />
                  </div>
                  <div class="min-w-0">
                    <p class="text-sm font-medium text-gray-700 truncate">{{ doc.original_name }}</p>
                    <p class="text-xs text-gray-500">{{ formatFileSize(doc.size) }}</p>
                  </div>
                </div>
                <button
                  type="button"
                  @click="removeFile(docType.key, index)"
                  class="p-1 hover:bg-green-200 rounded-full transition-colors"
                >
                  <X class="w-4 h-4 text-gray-500" />
                </button>
              </div>
            </div>

            <!-- Uploading Files Progress -->
            <div v-for="(uploadFile, key) in uploadingFiles" :key="key">
              <div v-if="key.startsWith(docType.key)" class="flex items-center justify-between bg-blue-50 rounded-lg px-4 py-2 border border-blue-200 mb-2">
                <div class="flex items-center space-x-3 min-w-0 flex-1">
                  <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Loader2 class="w-4 h-4 text-blue-600 animate-spin" />
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-sm font-medium text-gray-700 truncate">{{ uploadFile.name }}</p>
                    <div class="w-full bg-blue-200 rounded-full h-1.5 mt-1">
                      <div class="bg-blue-600 h-1.5 rounded-full transition-all" :style="{ width: `${uploadFile.progress}%` }"></div>
                    </div>
                  </div>
                </div>
                <span class="text-xs text-blue-600 ml-2">{{ uploadFile.progress }}%</span>
              </div>
            </div>

            <!-- Upload Button -->
            <label
              :class="[
                'flex items-center justify-center w-full px-4 py-6 border-2 border-dashed rounded-xl cursor-pointer transition-colors',
                errors[docType.key] || errors[`documents.${docType.key}`] ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
              ]"
            >
              <div class="text-center">
                <Upload class="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <span class="text-sm text-gray-600">Click to upload or drag and drop</span>
              </div>
              <input
                type="file"
                class="hidden"
                accept=".pdf,.jpg,.jpeg,.png"
                multiple
                @change="handleFileSelect(docType.key, $event)"
              />
            </label>
            <p v-if="errors[docType.key]" class="text-sm text-red-500">{{ errors[docType.key] }}</p>
            <p v-if="errors[`documents.${docType.key}`]" class="text-sm text-red-500">{{ Array.isArray(errors[`documents.${docType.key}`]) ? errors[`documents.${docType.key}`][0] : errors[`documents.${docType.key}`] }}</p>
          </div>

          <!-- Submit Button -->
          <Button
            type="submit"
            class="w-full bg-blue-600 hover:bg-blue-700 h-auto px-6 py-4 text-base font-semibold rounded-xl"
            :disabled="isSubmitting || isUploading"
          >
            <Loader2 v-if="isSubmitting || isUploading" class="w-5 h-5 mr-2 animate-spin" />
            {{ isUploading ? 'Uploading files...' : isSubmitting ? 'Submitting...' : 'Submit Registration' }}
          </Button>
        </form>
      </div>
    </div>
  </div>
</template>
